import { upload } from "@vercel/blob/client";
import type { SourceFile, SourceKind, Transcript } from "../types";

export interface IngestionProgress {
  totalFiles: number;
  completedFiles: number;
  totalBytes: number;
  uploadedBytes: number;
  throughputBytesPerSecond: number;
  uploadEtaSeconds: number;
  analysisEtaSeconds: number;
  currentFileName: string | null;
}

export interface IngestionResult {
  transcripts: Transcript[];
  sources: SourceFile[];
}

interface ExtractSourceResponse {
  kind: SourceKind;
  transcript: Transcript | null;
  processingNotes?: string;
  error?: string;
}

function createSourceId(): string {
  return `src_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
}

function sanitizePathSegment(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]/g, "-").slice(0, 120);
}

function estimateAnalysisSeconds(kind: SourceKind, sizeBytes: number): number {
  const sizeMb = sizeBytes / (1024 * 1024);
  if (kind === "audio") return Math.max(60, Math.round(sizeMb * 25));
  if (kind === "pdf") return Math.max(20, Math.round(sizeMb * 12));
  if (kind === "text") return Math.max(8, Math.round(sizeMb * 6));
  return Math.max(5, Math.round(sizeMb * 5));
}

function detectSourceKind(name: string, mimeType: string): SourceKind {
  const lower = name.toLowerCase();
  if (mimeType.startsWith("audio/") || /\.(mp3|wav|m4a|ogg|webm)$/i.test(lower)) {
    return "audio";
  }
  if (mimeType === "application/pdf" || lower.endsWith(".pdf")) {
    return "pdf";
  }
  if (mimeType.startsWith("text/") || /\.(txt|md|csv|tsv|json|rtf)$/i.test(lower)) {
    return "text";
  }
  return "unknown";
}

export async function ingestFiles(
  files: File[],
  onProgress?: (progress: IngestionProgress) => void,
): Promise<IngestionResult> {
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const sources: SourceFile[] = [];
  const transcripts: Transcript[] = [];
  const progressByFile = new Map<string, number>();
  let completedFiles = 0;
  const startedAt = Date.now();

  const emitProgress = (currentFileName: string | null) => {
    const uploadedBytes = Array.from(progressByFile.values()).reduce(
      (sum, value) => sum + value,
      0,
    );
    const elapsedSeconds = Math.max(1, (Date.now() - startedAt) / 1000);
    const throughputBytesPerSecond = uploadedBytes / elapsedSeconds;
    const remainingBytes = Math.max(0, totalBytes - uploadedBytes);
    const uploadEtaSeconds =
      throughputBytesPerSecond > 0
        ? Math.ceil(remainingBytes / throughputBytesPerSecond)
        : 0;
    const analysisEtaSeconds = Math.max(
      0,
      sources
        .filter((s) => s.status !== "ready")
        .reduce((sum, source) => {
          return sum + estimateAnalysisSeconds(source.kind, source.sizeBytes);
        }, 0),
    );

    onProgress?.({
      totalFiles: files.length,
      completedFiles,
      totalBytes,
      uploadedBytes,
      throughputBytesPerSecond,
      uploadEtaSeconds,
      analysisEtaSeconds,
      currentFileName,
    });
  };

  for (const file of files) {
    const sourceId = createSourceId();
    const uploadedAt = new Date().toISOString();
    const kind = detectSourceKind(file.name, file.type || "application/octet-stream");

    sources.push({
      id: sourceId,
      name: file.name,
      kind,
      mimeType: file.type || "application/octet-stream",
      sizeBytes: file.size,
      uploadedAt,
      blobUrl: "",
      status: "uploaded",
    });

    emitProgress(file.name);

    const pathname = `sources/${new Date().toISOString().slice(0, 10)}/${sourceId}-${sanitizePathSegment(file.name)}`;

    const uploadedBlob = await upload(pathname, file, {
      access: "private",
      contentType: file.type || "application/octet-stream",
      handleUploadUrl: "/api/blob-upload",
      multipart: true,
      onUploadProgress: (event) => {
        progressByFile.set(sourceId, event.loaded);
        emitProgress(file.name);
      },
    });

    const sourceIndex = sources.findIndex((source) => source.id === sourceId);
    if (sourceIndex === -1) continue;

    sources[sourceIndex] = {
      ...sources[sourceIndex],
      status: "processing",
      blobUrl: uploadedBlob.url,
    };

    emitProgress(file.name);

    const extractionResponse = await fetch("/api/extract-source", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: sourceId,
        name: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        uploadedAt,
        blobUrl: uploadedBlob.url,
      }),
    });

    const extraction = (await extractionResponse.json()) as ExtractSourceResponse;
    if (!extractionResponse.ok) {
      sources[sourceIndex] = {
        ...sources[sourceIndex],
        status: "failed",
        error: extraction.error || `Extraction failed (${extractionResponse.status})`,
      };
      completedFiles += 1;
      progressByFile.set(sourceId, file.size);
      emitProgress(null);
      continue;
    }

    sources[sourceIndex] = {
      ...sources[sourceIndex],
      kind: extraction.kind,
      status: "ready",
      processingNotes: extraction.processingNotes,
    };

    if (extraction.transcript?.content?.trim()) {
      transcripts.push(extraction.transcript);
    }

    completedFiles += 1;
    progressByFile.set(sourceId, file.size);
    emitProgress(null);
  }

  return { transcripts, sources };
}
